import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { socialApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { dataTablePageProps, usePagination } from '@/hooks/usePagination';
import { Input } from '@/components/ui/Input';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import { Modal } from '@/components/ui/Modal';
import { FileInput } from '@/components/ui/FileInput';
import { SocialTabNav } from '../components/SocialTabNav';
import { PUBLISHING_PLATFORMS } from '../config/platformCapabilities';
import type { SocialAccountResponse, SocialPostResponse } from '@/types/social';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const TABS = [
  { id: 'compose', label: 'Compose', to: '/social/publishing' },
  { id: 'drafts', label: 'Drafts', to: '/social/publishing?tab=drafts' },
  { id: 'queue', label: 'Publish queue', to: '/social/publishing?tab=queue' },
  { id: 'logs', label: 'Publishing logs', to: '/social/publishing?tab=logs' },
  { id: 'media', label: 'Media library', to: '/social/publishing?tab=media' },
  { id: 'approvals', label: 'Approvals', to: '/social/approvals' },
];

export function SocialPublishingCenterPage() {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'compose';
  const pagination = usePagination();
  const [data, setData] = useState<SocialPostResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<SocialAccountResponse[]>([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState<number[]>([]);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const publishingAccounts = accounts.filter((a) =>
    PUBLISHING_PLATFORMS.some((p) => a.platform.toUpperCase().includes(p.split('_')[0] ?? '')),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [posts, accs] = await Promise.all([
        socialApi.listPosts({ page: pagination.page, size: pagination.size }),
        socialApi.listAccounts(),
      ]);
      let rows = posts.content;
      if (tab === 'drafts') rows = rows.filter((p) => p.postStatus === 'DRAFT');
      if (tab === 'queue') rows = rows.filter((p) => p.postStatus === 'SCHEDULED' || p.postStatus === 'PENDING');
      setData(rows);
      pagination.applyPageResponse(posts);
      if (tab !== 'compose') pagination.applyClientCount(rows.length);
      setAccounts(accs.filter((a) => a.connected));
    } catch (err) {
      showApiError(err, 'Failed to load publishing');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleAccount = (id: number) => {
    setSelectedAccounts((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const columns: Column<SocialPostResponse>[] = [
    { key: 'id', header: '#' },
    { key: 'postStatus', header: 'Status' },
    { key: 'content', header: 'Content', render: (r) => r.content.slice(0, 80) },
    { key: 'channels', header: 'Platforms', render: (r) => r.channelAccountIds?.length ?? 0 },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.postStatus !== 'PUBLISHED' ? (
          <div className="flex flex-wrap gap-2">
            <button type="button" className="text-sm text-slate-600" onClick={() => void approve(r.id)}>
              Approve
            </button>
            <button type="button" className="text-sm text-primary-600" onClick={() => void publish(r.id)}>
              Publish
            </button>
            {(r.postStatus === 'FAILED' || r.postStatus === 'ERROR') && (
              <button type="button" className="text-sm text-amber-600" onClick={() => void retryPublish(r.id)}>
                Retry
              </button>
            )}
          </div>
        ) : (
          <span className="text-xs text-emerald-600">Published</span>
        ),
    },
  ];

  const create = async () => {
    if (!content.trim() || selectedAccounts.length === 0) {
      notify.error('Content and at least one platform required');
      return;
    }
    setSaving(true);
    try {
      const post = await socialApi.createPost({
        title,
        content,
        socialAccountIds: selectedAccounts,
        scheduledAt: scheduledAt || undefined,
      });
      if (mediaFile) await socialApi.uploadPostMedia(post.id, mediaFile);
      notify.success(scheduledAt ? 'Scheduled' : 'Draft saved');
      setOpen(false);
      void load();
    } catch (err) {
      showApiError(err, 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const publish = async (id: number) => {
    try {
      await socialApi.publishPost(id);
      notify.success('Published');
      void load();
    } catch (err) {
      showApiError(err, 'Publish failed — use Retry after fixing channel tokens');
    }
  };

  const retryPublish = async (id: number) => {
    try {
      await socialApi.publishPost(id);
      notify.success('Publish retry completed');
      void load();
    } catch (err) {
      showApiError(err, 'Retry failed — open Connection Center to refresh tokens');
    }
  };

  const approve = async (id: number) => {
    try {
      await socialApi.approvePost(id);
      notify.success('Approved');
      void load();
    } catch (err) {
      showApiError(err, 'Approval failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Publishing Center"
        description="Compose, schedule, approve, and publish across connected networks"
        breadcrumbs={[{ label: 'Social', to: '/social' }, { label: 'Publishing' }]}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/social/calendar">
              <Button variant="secondary">Calendar</Button>
            </Link>
            <Button onClick={() => setOpen(true)} disabled={publishingAccounts.length === 0}>
              <AddIcon fontSize="small" /> Compose
            </Button>
          </div>
        }
      />
      <SocialTabNav tabs={TABS} className="mb-4" />

      {tab === 'media' ? (
        <div className="card p-6 text-sm text-slate-600 dark:text-slate-400">
          <p>Upload and reuse images and videos in the dedicated Media Library.</p>
          <Link to="/social/media" className="mt-3 inline-block">
            <Button>Open Media Library</Button>
          </Link>
        </div>
      ) : tab === 'logs' ? (
        <div className="card p-6">
          <h3 className="text-sm font-semibold">Publishing logs</h3>
          <ul className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-400">
            {data.slice(0, 8).map((p) => (
              <li key={p.id}>
                #{p.id} · {p.postStatus} · {p.createdAt?.slice(0, 16) ?? '—'}
              </li>
            ))}
            {data.length === 0 && <li>No log entries yet.</li>}
          </ul>
        </div>
      ) : (
        <>
          {publishingAccounts.length === 0 && (
            <p className="mb-4 text-sm text-amber-700 dark:text-amber-300">
              Connect publishing-capable channels in Settings. WhatsApp is inbox-only.
            </p>
          )}
          <DataTable
            columns={columns}
            data={data}
            keyField="id"
            loading={loading}
            emptyMessage={tab === 'drafts' ? 'No drafts' : 'No posts in queue'}
            {...dataTablePageProps(pagination)}
          />
        </>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Compose post"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => void create()} loading={saving}>Save draft / schedule</Button>
          </>
        }
      >
        <div className="grid gap-4">
          <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Input label="Content" value={content} onChange={(e) => setContent(e.target.value)} className="min-h-[100px]" />
          <DateTimePicker label="Schedule" value={scheduledAt} onChange={setScheduledAt} />
          <div>
            <p className="mb-2 text-sm font-medium">Platform selection</p>
            <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto">
              {publishingAccounts.map((a) => (
                <label key={a.id} className="flex items-center gap-2 rounded border px-2 py-1 text-sm dark:border-slate-700">
                  <input
                    type="checkbox"
                    checked={selectedAccounts.includes(a.id)}
                    onChange={() => toggleAccount(a.id)}
                  />
                  {a.platform}
                </label>
              ))}
            </div>
          </div>
          <FileInput
            label="Media attachment"
            value={mediaFile}
            accept="image/*,video/*"
            description="Images or video for this post"
            placeholder="Optional media file"
            onChange={(files) => setMediaFile(files[0] ?? null)}
          />
        </div>
      </Modal>
    </div>
  );
}
